import React, { useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  ScrollView,
  Linking,
} from 'react-native';
import { useNavigation } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import { Ionicons } from '@expo/vector-icons';
import { RootStackParamList } from '../../types/navigation';
import { useAuth } from '../../hooks/useAuth';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useTheme } from '../../hooks/useTheme';
import ResponsiveContainer from '../../components/shared/ResponsiveContainer';

interface FAQItem {
  question: string;
  answer: string;
  category: 'billing' | 'subscription' | 'payment' | 'general';
}

interface SupportOption {
  title: string;
  description: string;
  icon: string;
  action: () => void;
  color?: string;
}

const BillingSupportScreen = () => {
  const navigation =
    useNavigation<NativeStackNavigationProp<RootStackParamList>>();
  const { user, subscriptionPlan } = useAuth();
  const insets = useSafeAreaInsets();
  const { colors } = useTheme();
  const [expandedFAQ, setExpandedFAQ] = useState<number | null>(null);

  const faqItems: FAQItem[] = [
    {
      question: 'How do I cancel my subscription?',
      answer:
        'You can cancel your subscription at any time by going to Settings > Billing & Subscription > Cancel Subscription. Your access will continue until the end of your current billing period.',
      category: 'subscription',
    },
    {
      question: 'Can I get a refund?',
      answer:
        'We offer refunds within 7 days of purchase for new subscriptions. Please contact our support team with your order details for assistance.',
      category: 'billing',
    },
    {
      question: 'How do I update my payment method?',
      answer:
        'Go to Settings > Billing & Subscription > Manage Billing to update your payment information. You can change your card details or add a new payment method.',
      category: 'payment',
    },
    {
      question: 'Why was I charged twice?',
      answer:
        'If you see duplicate charges, it might be due to a pending authorization that will be released within 3-5 business days. Contact us if the charge remains after this period.',
      category: 'billing',
    },
    {
      question: 'How do I start a free trial?',
      answer:
        "You can start a 7-day free trial by going to Settings > Billing & Subscription and selecting 'Start Free Trial'. No payment is required during the trial period.",
      category: 'subscription',
    },
    {
      question: 'What happens when my trial ends?',
      answer:
        "When your trial ends, you'll be automatically charged for the Pro plan unless you cancel before the trial expires. You can cancel anytime during the trial period.",
      category: 'subscription',
    },
    {
      question: 'Can I change my subscription plan?',
      answer:
        'Yes! You can upgrade or downgrade your plan at any time. Go to Settings > Billing & Subscription to view all available plans and make changes.',
      category: 'subscription',
    },
    {
      question: 'How do I view my billing history?',
      answer:
        "Your billing history is available in the Billing & Subscription section. We're working on adding detailed transaction history to the app.",
      category: 'billing',
    },
    {
      question: 'Is my payment information secure?',
      answer:
        'Yes, we use industry-standard encryption and secure payment processors to protect your payment information. We never store your full card details on our servers.',
      category: 'payment',
    },
    {
      question: 'What payment methods do you accept?',
      answer:
        'We accept all major credit cards (Visa, MasterCard, American Express) and debit cards. We also support Apple Pay and Google Pay on supported devices.',
      category: 'payment',
    },
  ];

  const supportOptions: SupportOption[] = [
    {
      title: 'Email Support',
      description: 'Get help via email within 24 hours',
      icon: 'mail-outline',
      action: () => {
        Linking.openURL(
          'mailto:support@mail.mycoloursense.com?subject=Billing%20Support%20Request'
        );
      },
      color: colors.supportText,
    },
    // {
    //   title: "Live Chat",
    //   description: "Chat with our support team",
    //   icon: "chatbubble-outline",
    //   action: () => {
    //     Alert.alert(
    //       "Live Chat",
    //       "Live chat is available Monday-Friday, 9 AM - 6 PM EST. Would you like to start a chat?",
    //       [
    //         { text: "Cancel", style: "cancel" },
    //         { text: "Start Chat", onPress: () => {
    //           // This would integrate with a live chat service
    //           Alert.alert("Live Chat", "Connecting you to our support team...");
    //         }}
    //       ]
    //     );
    //   },
    //   color: colors.success
    // },
    // {
    //   title: "Phone Support",
    //   description: "Call us for immediate assistance",
    //   icon: "call-outline",
    //   action: () => {
    //     Alert.alert(
    //       "Phone Support",
    //       "Call us at +1 (555) 123-4567\nAvailable Monday-Friday, 9 AM - 6 PM EST",
    //       [
    //         { text: "Cancel", style: "cancel" },
    //         { text: "Call Now", onPress: () => {
    //           Linking.openURL('tel:+15551234567');
    //         }}
    //       ]
    //     );
    //   },
    //   color: colors.theme_color
    // },
    {
      title: 'Help Center',
      description: 'Browse our knowledge base',
      icon: 'library-outline',
      action: () => {
        navigation.navigate('HelpCenter');
      },
      color: colors.helpCenterText,
    },
  ];

  const handleFAQToggle = (index: number) => {
    setExpandedFAQ(expandedFAQ === index ? null : index);
  };

  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'billing':
        return colors.billing;
      case 'subscription':
        return colors.supportText;
      case 'payment':
        return colors.success;
      default:
        return colors.cardText;
    }
  };

  const getCategoryLabel = (category: string) => {
    switch (category) {
      case 'billing':
        return 'Billing';
      case 'subscription':
        return 'Subscription';
      case 'payment':
        return 'Payment';
      default:
        return 'General';
    }
  };

  return (
    <ResponsiveContainer style={{ backgroundColor: colors.background }}>
      <ScrollView
        style={styles.scrollView}
        contentContainerStyle={{
          paddingBottom: 40 + insets.bottom,
          paddingHorizontal: 16,
          paddingTop: 16,
        }}
        showsVerticalScrollIndicator={false}
      >
        {/* Header */}
        <View style={styles.header}>
          <Text style={[styles.title, { color: colors.text }]}>
            Billing Support
          </Text>
          <Text style={[styles.subtitle, { color: colors.secondary }]}>
            Get help with your subscription and billing questions
          </Text>
        </View>

        {/* Current Plan Info */}
        <View
          style={[
            styles.currentPlanCard,
            { backgroundColor: colors.card, borderColor: colors.border },
          ]}
        >
          <Text style={[styles.currentPlanTitle, { color: colors.text }]}>
            Your Current Plan
          </Text>
          <View style={styles.currentPlanInfo}>
            <Ionicons name="card-outline" size={24} color={colors.primary} />
            <View style={styles.currentPlanDetails}>
              <Text style={[styles.planName, { color: colors.text }]}>
                {subscriptionPlan === 'free'
                  ? 'Free Plan'
                  : subscriptionPlan === 'freemium_trial'
                    ? 'Free Trial'
                    : subscriptionPlan === 'pro'
                      ? 'Pro Plan'
                      : 'Pay-per-scan'}
              </Text>
              <Text style={[styles.planEmail, { color: colors.secondary }]}>
                {user?.email}
              </Text>
            </View>
          </View>
        </View>

        {/* Quick Support Options */}
        <View style={styles.section}>
          <Text style={[styles.sectionTitle, { color: colors.primary }]}>
            Get Help
          </Text>
          <View style={styles.supportOptionsContainer}>
            {supportOptions.map((option, index) => (
              <TouchableOpacity
                key={index}
                style={[
                  styles.supportOption,
                  { backgroundColor: colors.card, borderColor: colors.border },
                ]}
                onPress={option.action}
              >
                <View
                  style={[
                    styles.supportIcon,
                    { backgroundColor: option.color },
                  ]}
                >
                  <Ionicons
                    name={option.icon as keyof typeof Ionicons.glyphMap}
                    size={24}
                    color={colors.background}
                  />
                </View>
                <View style={styles.supportContent}>
                  <Text style={[styles.supportTitle, { color: colors.text }]}>
                    {option.title}
                  </Text>
                  <Text
                    style={[
                      styles.supportDescription,
                      { color: colors.secondary },
                    ]}
                  >
                    {option.description}
                  </Text>
                </View>
                <Ionicons
                  name="chevron-forward"
                  size={20}
                  color={colors.secondary}
                />
              </TouchableOpacity>
            ))}
          </View>
        </View>

        {/* FAQ Section */}
        <View style={styles.section}>
          <Text style={[styles.sectionTitle, { color: colors.primary }]}>
            Frequently Asked Questions
          </Text>
          <View style={styles.faqContainer}>
            {faqItems.map((item, index) => (
              <View
                key={index}
                style={[
                  styles.faqItem,
                  { backgroundColor: colors.card, borderColor: colors.border },
                ]}
              >
                <TouchableOpacity
                  style={styles.faqHeader}
                  onPress={() => handleFAQToggle(index)}
                >
                  <View style={styles.faqHeaderContent}>
                    <View
                      style={[
                        styles.categoryBadge,
                        { backgroundColor: getCategoryColor(item.category) },
                      ]}
                    >
                      <Text
                        style={[
                          styles.categoryText,
                          { color: colors.background },
                        ]}
                      >
                        {getCategoryLabel(item.category)}
                      </Text>
                    </View>
                    <Text style={[styles.faqQuestion, { color: colors.text }]}>
                      {item.question}
                    </Text>
                  </View>
                  <Ionicons
                    name={expandedFAQ === index ? 'chevron-up' : 'chevron-down'}
                    size={20}
                    color={colors.secondary}
                  />
                </TouchableOpacity>

                {expandedFAQ === index && (
                  <View style={styles.faqAnswer}>
                    <Text
                      style={[
                        styles.faqAnswerText,
                        { color: colors.secondary },
                      ]}
                    >
                      {item.answer}
                    </Text>
                  </View>
                )}
              </View>
            ))}
          </View>
        </View>

        {/* Contact Information */}
        <View
          style={[
            styles.contactCard,
            { backgroundColor: colors.card, borderColor: colors.border },
          ]}
        >
          <Text style={[styles.contactTitle, { color: colors.text }]}>
            Still Need Help?
          </Text>
          <Text style={[styles.contactText, { color: colors.secondary }]}>
            Our support team is here to help you with any billing or
            subscription issues. We typically respond within 24 hours.
          </Text>
          <View style={styles.contactInfo}>
            <View style={styles.contactItem}>
              <Ionicons name="mail-outline" size={16} color={colors.primary} />
              <Text style={[styles.contactDetail, { color: colors.secondary }]}>
                support@mail.mycoloursense.com
              </Text>
            </View>
            <View style={styles.contactItem}>
              <Ionicons name="time-outline" size={16} color={colors.primary} />
              <Text style={[styles.contactDetail, { color: colors.secondary }]}>
                Mon-Fri, 9 AM - 6 PM EST
              </Text>
            </View>
          </View>
        </View>
      </ScrollView>
    </ResponsiveContainer>
  );
};

const styles = StyleSheet.create({
  scrollView: {
    flex: 1,
  },
  header: {
    alignItems: 'center',
    marginBottom: 24,
  },
  title: {
    fontSize: 28,
    fontWeight: '700',
    marginBottom: 8,
    textAlign: 'center',
  },
  subtitle: {
    fontSize: 16,
    textAlign: 'center',
    lineHeight: 24,
  },
  currentPlanCard: {
    padding: 20,
    borderRadius: 12,
    borderWidth: 1,
    marginBottom: 24,
  },
  currentPlanTitle: {
    fontSize: 16,
    fontWeight: '600',
    marginBottom: 12,
  },
  currentPlanInfo: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  currentPlanDetails: {
    marginLeft: 12,
    flex: 1,
  },
  planName: {
    fontSize: 18,
    fontWeight: '600',
    marginBottom: 4,
  },
  planEmail: {
    fontSize: 14,
  },
  section: {
    marginBottom: 24,
  },
  sectionTitle: {
    fontSize: 20,
    fontWeight: '600',
    marginBottom: 16,
  },
  supportOptionsContainer: {
    gap: 12,
  },
  supportOption: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 16,
    borderRadius: 12,
    borderWidth: 1,
  },
  supportIcon: {
    width: 48,
    height: 48,
    borderRadius: 24,
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 12,
  },
  supportContent: {
    flex: 1,
  },
  supportTitle: {
    fontSize: 16,
    fontWeight: '600',
    marginBottom: 4,
  },
  supportDescription: {
    fontSize: 14,
  },
  faqContainer: {
    gap: 12,
  },
  faqItem: {
    borderRadius: 12,
    borderWidth: 1,
    overflow: 'hidden',
  },
  faqHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: 16,
  },
  faqHeaderContent: {
    flex: 1,
  },
  categoryBadge: {
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 8,
    alignSelf: 'flex-start',
    marginBottom: 8,
  },
  categoryText: {
    fontSize: 12,
    fontWeight: '600',
  },
  faqQuestion: {
    fontSize: 16,
    fontWeight: '500',
    lineHeight: 22,
  },
  faqAnswer: {
    paddingHorizontal: 16,
    paddingBottom: 16,
  },
  faqAnswerText: {
    fontSize: 14,
    lineHeight: 20,
  },
  contactCard: {
    padding: 20,
    borderRadius: 12,
    borderWidth: 1,
  },
  contactTitle: {
    fontSize: 18,
    fontWeight: '600',
    marginBottom: 8,
  },
  contactText: {
    fontSize: 14,
    lineHeight: 20,
    marginBottom: 16,
  },
  contactInfo: {
    gap: 8,
  },
  contactItem: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  contactDetail: {
    fontSize: 14,
    marginLeft: 8,
  },
});

export default BillingSupportScreen;
